import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Shield, Info } from "lucide-react";
import type { MortgageData } from "../../CalculatorTabs";
import { calculateStateVAExemption } from "@/utils/calculator/stateVAExemptions";

interface VAExemptionBreakdownProps {
  mortgageData: MortgageData;
}

export const VAExemptionBreakdown: React.FC<VAExemptionBreakdownProps> = ({ mortgageData }) => {
  const { state, vaDisabilityStatus, vaDisabilityRatingPercentage, veteranAge, homePrice } = mortgageData;
  
  if (!vaDisabilityStatus || vaDisabilityStatus === 'none') {
    return null;
  }
  
  const exemption = calculateStateVAExemption(
    state,
    vaDisabilityStatus as "none" | "partial" | "full" | null,
    vaDisabilityRatingPercentage,
    veteranAge,
    homePrice
  );
  
  if (exemption.totalExemption === 0) {
    return null;
  }
  
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          VA Property Tax Exemption Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Home Value</span>
          <span className="font-semibold">${homePrice.toLocaleString()}</span>
        </div>
        
        {exemption.baseExemption > 0 && (
          <div className="flex justify-between text-sm text-primary">
            <span>
              Base Exemption 
              {vaDisabilityStatus === 'partial' && vaDisabilityRatingPercentage && 
                ` (${vaDisabilityRatingPercentage}% rating)`}
            </span>
            <span className="font-semibold">-${exemption.baseExemption.toLocaleString()}</span>
          </div>
        )}
        
        {exemption.age65Bonus > 0 && (
          <div className="flex justify-between text-sm text-primary">
            <span>Age 65+ Bonus</span>
            <span className="font-semibold">-${exemption.age65Bonus.toLocaleString()}</span>
          </div>
        )}
        
        <Separator />
        
        <div className="flex justify-between text-base font-bold">
          <span>Taxable Value</span>
          <span className={exemption.taxableValue === 0 ? "text-primary" : ""}>
            ${exemption.taxableValue.toLocaleString()}
          </span>
        </div>
        
        <Alert className="bg-muted/50 border-muted">
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            {exemption.description}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
